"use client";

import type { UIMessage } from "@ai-sdk/react";
import { PaperclipIcon } from "lucide-react";
import Image from "next/image";
import { FaviconStack } from "@/components/ai-elements/favicon-stack";
import { Message, MessageContent, AssistantAvatar, UserAvatar } from "@/components/ai-elements/message";
import { Response } from "@/components/ai-elements/response";

interface ChatMessagesProps {
  messages: UIMessage[];
  isStreaming?: boolean;
  showAssistantSkeletonWhileWaiting?: boolean;
}

interface SourceItem {
  url: string;
  title: string;
  publishedDate?: string;
}

interface WebSearchToolOutput {
  sources?: SourceItem[];
}

function hasPendingToolExecution(parts: UIMessage["parts"]): boolean {
  if (!Array.isArray(parts)) return false;

  return parts.some((part) => {
    const type = typeof part.type === "string" ? part.type : "";
    if (!type.startsWith("tool-")) return false;
    const toolPart = part as Record<string, unknown>;
    return !("output" in toolPart) || toolPart.output == null;
  });
}

/**
 * Extract sources from webSearch tool results
 * Sources are already deduplicated by the tool
 */
function extractWebSearchSources(parts: UIMessage["parts"]): SourceItem[] {
  const sources: SourceItem[] = [];

  if (!Array.isArray(parts)) {
    return sources;
  }

  for (const part of parts) {
    const type = part.type as string;
    if (type === "tool-webSearch") {
      const output = (part as { output?: WebSearchToolOutput }).output;
      if (output?.sources && Array.isArray(output.sources)) {
        sources.push(...output.sources);
      }
    }
  }

  return sources;
}

/**
 * Extract source-url parts from AI SDK
 */
function extractAiSdkSources(parts: UIMessage["parts"]): SourceItem[] {
  const sources: SourceItem[] = [];

  if (!Array.isArray(parts)) {
    return sources;
  }

  for (const part of parts) {
    if (part.type === "source-url") {
      const sourcePart = part as { url: string; title?: string };
      sources.push({
        url: sourcePart.url,
        title: sourcePart.title || sourcePart.url,
      });
    }
  }

  return sources;
}

/**
 * Extract file parts from message
 */
function extractFileParts(parts: UIMessage["parts"]) {
  if (!Array.isArray(parts)) {
    return [];
  }
  return parts.filter((part) => part.type === "file");
}

export function ChatMessages({
  messages,
  isStreaming = false,
  showAssistantSkeletonWhileWaiting = false,
  textAnimationState,
  ttsPendingMessageIds,
}: ChatMessagesProps & {
  textAnimationState?: {
    messageId: string;
    currentIndex: number;
    fullText: string;
  };
  ttsPendingMessageIds?: Set<string>;
}) {
  if (!Array.isArray(messages)) {
    return null;
  }
  const isWaitingForAssistantReply =
    isStreaming && messages[messages.length - 1]?.role === "user";
  
  return (
    <>
      {(() => {
        const latestAssistantMessageId = [...messages]
          .reverse()
          .find((msg) => msg.role === "assistant")?.id;

        return messages.map(({ parts, ...message }, index) => {
          // Defensive check: ensure parts is an array
          if (!Array.isArray(parts)) {
            console.warn("Message parts is not an array:", message.id, parts);
            return null;
          }

          // Extract text parts
          const textParts = parts.filter((part) => part.type === "text");
          let textContent = textParts
            .map((part) => {
              if (part.type === "text" && typeof part.text === "string") {
                const text = part.text;
                // Prevent displaying raw JSON message objects as text
                // Check if text looks like a stringified message object
                if (text.trim().startsWith("{") && text.includes('"id"') && text.includes('"role"') && text.includes('"parts"')) {
                  try {
                    const parsed = JSON.parse(text);
                    // If it's a message object, extract text from parts instead
                    if (parsed.parts && Array.isArray(parsed.parts)) {
                      const extractedText = parsed.parts
                        .filter((p: { type?: string; text?: string }) => p.type === "text" && typeof p.text === "string")
                        .map((p: { text: string }) => p.text)
                        .join("");
                      return extractedText || "";
                    }
                  } catch {
                    // Not valid JSON, return as-is
                  }
                }
                return text;
              }
              return "";
            })
            .join("");

          // Apply text animation if this message is being animated
          const isBeingAnimated =
            textAnimationState && textAnimationState.messageId === message.id;
          const shouldApplyTextAnimation =
            Boolean(isBeingAnimated) && !isWaitingForAssistantReply;
          const isTtsPending = ttsPendingMessageIds?.has(message.id) ?? false;
          const isLatestAssistantMessage =
            message.role === "assistant" &&
            message.id === latestAssistantMessageId &&
            index === messages.length - 1;
          const hasRenderableText = textContent.trim().length > 0;
          const hasPendingTool = hasPendingToolExecution(parts);
          const shouldShowAlignedSkeleton =
            isLatestAssistantMessage &&
            !hasRenderableText &&
            (isTtsPending ||
              (isStreaming && hasPendingTool) ||
              (shouldApplyTextAnimation &&
                (textAnimationState?.currentIndex ?? 0) === 0));
          if (shouldApplyTextAnimation && textAnimationState) {
            // Use the animated text instead of the streaming text
            textContent = textAnimationState.fullText.slice(
              0,
              textAnimationState.currentIndex,
            );
          }

          // Extract file parts
          const fileParts = extractFileParts(parts);

          // Extract sources from AI SDK and webSearch
          const aiSdkSources = extractAiSdkSources(parts);

          // Extract sources from webSearch tool results (already deduplicated)
          const webSearchSources = extractWebSearchSources(parts);

          // Combine sources and deduplicate between AI SDK and webSearch sources
          const allSources = [...aiSdkSources, ...webSearchSources];
          const uniqueSources = allSources.filter(
            (source, index, self) =>
              index === self.findIndex((s) => s.url === source.url),
          );

          // Check if this is the last (currently streaming) message
          const isLastMessage = index === messages.length - 1;

          // Show sources only after response is finished (not on the currently streaming message)
          const shouldShowSources =
            uniqueSources.length > 0 &&
            message.role === "assistant" &&
            (!isLastMessage || !isStreaming);

          return (
            <div key={message.id}>
              {/* Render file attachments */}
              {fileParts.length > 0 && (
                <Message from={message.role}>
                  <MessageContent variant="contained" className="max-w-[90%] sm:max-w-[80%]">
                    <div className="flex flex-wrap gap-2 mb-2">
                      {fileParts.map((part) => {
                        if (part.type !== "file") return null;

                        const file = part as {
                          type: "file";
                          url?: string;
                          mediaType?: string;
                          filename?: string;
                        };

                        // Create a unique key from file properties
                        const fileKey = `${file.url}-${file.filename}`;
                        const isImage = file.mediaType?.startsWith("image/");

                        if (isImage && file.url) {
                          return (
                            <div
                              key={fileKey}
                              className="relative rounded-lg border overflow-hidden"
                            >
                              <Image
                                src={file.url}
                                alt={file.filename || "attachment"}
                                className="max-w-xs max-h-48 object-cover"
                                width={300}
                                height={192}
                                unoptimized
                              />
                            </div>
                          );
                        }

                        return (
                          <div
                            key={fileKey}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--mindup-forest)]/10 dark:border-white/10 bg-[var(--mindup-cream-warm)] dark:bg-[var(--mindup-forest)]"
                          >
                            <PaperclipIcon className="size-4 shrink-0 text-[var(--mindup-forest)]/50 dark:text-[var(--mindup-cream)]/50" />
                            <span className="text-sm font-medium text-[var(--mindup-forest)] dark:text-[var(--mindup-cream)]">
                              {file.filename || "Unknown file"}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </MessageContent>
                </Message>
              )}

              {/* Render text content in message */}
              {(textParts.length > 0 || shouldShowAlignedSkeleton) && (
                <Message from={message.role}>
                  {message.role === "assistant" ? <AssistantAvatar /> : null}
                  <MessageContent variant="contained" className="max-w-[90%] sm:max-w-[80%]">
                    {shouldShowAlignedSkeleton ? (
                      <div
                        aria-label="Assistant response loading"
                        className="space-y-2 py-1 min-w-[220px]"
                      >
                        <div className="h-3 w-52 rounded-md bg-gray-300/80 dark:bg-white/25 animate-pulse" />
                        <div className="h-3 w-40 rounded-md bg-gray-300/65 dark:bg-white/20 animate-pulse" />
                      </div>
                    ) : (
                      <Response>{textContent}</Response>
                    )}
                  </MessageContent>
                  {message.role === "user" ? <UserAvatar /> : null}
                </Message>
              )}

              {/* Render sources as stacked favicons - show immediately when available */}
              {shouldShowSources && (
                <div className="max-w-[90%] sm:max-w-[80%]">
                  <FaviconStack sources={uniqueSources} />
                </div>
              )}
            </div>
          );
        });
      })()}
      {showAssistantSkeletonWhileWaiting &&
        isStreaming &&
        messages[messages.length - 1]?.role === "user" && (
          <Message from="assistant">
            <AssistantAvatar />
            <MessageContent variant="contained" className="max-w-[90%] sm:max-w-[80%]">
              <div
                aria-label="Assistant response loading"
                className="space-y-2 py-1 min-w-[220px]"
              >
                <div className="h-3 w-52 rounded-md bg-gray-300/80 dark:bg-white/25 animate-pulse" />
                <div className="h-3 w-40 rounded-md bg-gray-300/65 dark:bg-white/20 animate-pulse" />
              </div>
            </MessageContent>
          </Message>
        )}
    </>
  );
}
